import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Edit, Trash2, Tv, Building2, MapPin, Tag, Hash,
  Monitor, Wifi, Calendar, FileText, Truck
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const TelevisorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('micro', 'tvs');
  
  const [tv, setTv] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formOptions, setFormOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [], ubicaciones: [], proveedores: [] });
  const [saving, setSaving] = useState(false);
  
  // Delete modal
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchTv = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/tvs/${id}`);
      setTv(response.data);
    } catch (error) {
      toast.error('Error al cargar el televisor');
      navigate('/micro/tvs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/tvs/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchTv();
    fetchFormOptions();
  }, [id]);

  const openEditModal = () => {
    setFormData({
      empresa_id: tv.empresa_id?.toString() || '',
      site_id: tv.site_id?.toString() || '',
      brand_id: tv.brand_id?.toString() || '',
      asset_tag: tv.asset_tag || '',
      serial_number: tv.serial_number || '',
      modelo: tv.modelo || '',
      pulgadas: tv.pulgadas?.toString() || '',
      ip_address: tv.ip_address || '',
      ubicacion: tv.ubicacion || '',
      status_id: tv.status_id?.toString() || '',
      location_id: tv.location_id?.toString() || '',
      supplier_id: tv.supplier_id?.toString() || '',
      purchase_date: tv.purchase_date || '',
      comments: tv.comments || ''
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        pulgadas: formData.pulgadas ? parseInt(formData.pulgadas) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
      };

      await api.put(`/assets/tvs/${id}`, payload);
      toast.success('Televisor actualizado exitosamente');
      setEditModal(false);
      fetchTv();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/tvs/${id}`);
      toast.success('Televisor eliminado exitosamente');
      navigate('/micro/tvs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) return <span className="badge badge-warning">Sin Estado</span>;
    const lower = statusName.toLowerCase();
    if (lower.includes('activo') || lower.includes('active')) {
      return <span className="badge badge-success">{statusName}</span>;
    }
    if (lower.includes('inactivo') || lower.includes('inactive') || lower.includes('baja')) {
      return <span className="badge badge-danger">{statusName}</span>;
    }
    return <span className="badge badge-default">{statusName}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!tv) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/micro/tvs')}>
              <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <Tv className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <h1 className="text-2xl font-bold">{tv.brand_name || 'TV'} {tv.modelo || ''}</h1>
              </div>
              <p className="text-muted-foreground font-mono">{tv.asset_tag}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={openEditModal}>
                <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteModal(true)}>
                <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Summary Bar */}
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                {getStatusBadge(tv.status_name)}
              </div>
              {tv.brand_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Tag className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{tv.brand_name}</span>
                </div>
              )}
              {tv.pulgadas && (
                <div className="flex items-center gap-2 text-sm">
                  <Monitor className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{tv.pulgadas}"</span>
                </div>
              )}
              {tv.ip_address && (
                <div className="flex items-center gap-2 text-sm">
                  <Wifi className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono">{tv.ip_address}</span>
                </div>
              )}
              {tv.site_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{tv.site_name}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detail Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Identificación */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Hash className="h-4 w-4" strokeWidth={1.5} />
                Identificación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Asset Tag</span>
                <span className="font-mono">{tv.asset_tag || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Serial Number</span>
                <span className="font-mono">{tv.serial_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Marca</span>
                <span>{tv.brand_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Modelo</span>
                <span>{tv.modelo || '-'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Especificaciones */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="h-4 w-4" strokeWidth={1.5} />
                Especificaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pulgadas</span>
                <span>{tv.pulgadas ? `${tv.pulgadas}"` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">IP Address</span>
                <span className="font-mono">{tv.ip_address || '-'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Ubicación y Organización */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4" strokeWidth={1.5} />
                Ubicación y Organización
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Empresa</span>
                <span>{tv.empresa_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Site</span>
                <span>{tv.site_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ubicación (FK)</span>
                <span>{tv.location_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ubicación (texto)</span>
                <span>{tv.ubicacion || '-'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Compra y Notas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" strokeWidth={1.5} />
                Compra y Notas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Proveedor</span>
                <span>{tv.supplier_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fecha de Compra</span>
                <span>{tv.purchase_date || '-'}</span>
              </div>
              {tv.comments && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Comentarios</p>
                  <p className="text-sm">{tv.comments}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit Modal */}
        <Dialog open={editModal} onOpenChange={setEditModal}>
          <DialogContent className="max-w-2xl" aria-describedby="edit-tv-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" strokeWidth={1.5} />
                Editar Televisor
              </DialogTitle>
            </DialogHeader>
            <p id="edit-tv-description" className="text-sm text-muted-foreground">Modifique los datos del televisor.</p>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input value={formData.asset_tag} onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands.map(b => (
                      <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input value={formData.modelo} onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Pulgadas</Label>
                <Input type="number" value={formData.pulgadas} onChange={(e) => setFormData(prev => ({ ...prev, pulgadas: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input value={formData.ip_address} onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Empresa *</Label>
                <Select value={formData.empresa_id} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Site *</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación (FK)</Label>
                <Select value={formData.location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación (texto)</Label>
                <Input value={formData.ubicacion} onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Comentarios</Label>
                <Textarea value={formData.comments} onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
          <DialogContent aria-describedby="delete-tv-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Televisor
              </DialogTitle>
            </DialogHeader>
            <p id="delete-tv-description" className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar el televisor <strong>{tv.asset_tag}</strong>?
              <br />
              <span className="text-sm">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TelevisorDetail;
